import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { errMsg } from "../../lib/portfolio";

export function PublishControls({ project, onChanged }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const needsOverride = ["draft", "in_review"].includes(project.status);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setOpen(false); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const s = project.status;
  return (
    <div className="flex flex-wrap gap-2" data-testid="portfolio-publish-controls">
      {["draft", "unpublished"].includes(s) && (
        <Button variant="outline" className="border-erf-borderCream text-erf-dark" disabled={busy} data-testid="portfolio-submit-review-btn"
          onClick={() => run(() => api.post(`/admin/portfolio/projects/${project.id}/submit`), "Sent for review.")}>Send for review</Button>
      )}
      {!["published", "archived"].includes(s) && (
        <Button className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" onClick={() => { setConfirm(false); setReason(""); setOpen(true); }}
          data-testid="portfolio-publish-btn">Publish…</Button>
      )}
      {s === "published" && (
        <Button variant="outline" className="border-erf-borderCream text-erf-dark" disabled={busy} data-testid="portfolio-unpublish-btn"
          onClick={() => window.confirm("Remove this project from the public website?") &&
            run(() => api.post(`/admin/portfolio/projects/${project.id}/unpublish`), "Unpublished.")}>Unpublish</Button>
      )}
      {s !== "archived" && s !== "published" && (
        <Button variant="outline" className="border-[#FECACA] text-[#991B1B]" disabled={busy} data-testid="portfolio-archive-btn"
          onClick={() => window.confirm("Archive this project? It will be hidden from lists.") &&
            run(() => api.delete(`/admin/portfolio/projects/${project.id}`), "Archived.")}>Archive</Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FEFCF7] text-erf-dark" data-testid="portfolio-publish-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-erf-dark">Publish to the public website?</DialogTitle>
            <DialogDescription className="font-nav text-erf-dark/80">
              Only approved captions and processed files (metadata removed) are shown. You can unpublish at any time.
            </DialogDescription>
          </DialogHeader>
          {needsOverride && (
            <div className="space-y-1">
              <p className="text-sm font-nav text-[#78350F]">No reviewer has approved this project yet. Give a reason to publish anyway (10+ characters).</p>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500}
                aria-label="Reason for publishing without review" className="bg-white text-erf-dark" data-testid="portfolio-publish-override-reason" />
            </div>
          )}
          <label className="flex items-center gap-2 min-h-[44px] text-sm font-nav text-erf-dark">
            <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="w-5 h-5"
              data-testid="portfolio-publish-confirm-checkbox" />
            I confirm this project may appear on the public website.
          </label>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="border-erf-borderCream text-erf-dark" onClick={() => setOpen(false)} data-testid="portfolio-publish-cancel">Cancel</Button>
            <Button className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="portfolio-publish-confirm"
              disabled={busy || !confirm || (needsOverride && reason.trim().length < 10)}
              onClick={() => run(() => api.post(`/admin/portfolio/projects/${project.id}/publish`,
                { confirm: true, override: needsOverride, override_reason: reason }), "Published.")}>Publish now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
